import { useCallback, useRef } from 'react';
import { authFetch } from '@/lib/auth';
import { projectsApi } from '@/lib/api';
import type { Sender } from '@/types/api';

export interface CachedProjectData {
  projectId: number;
  promptId: string | null;
  senders: Sender[];
  fetchedAt: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function useProjectCache() {
  const cacheRef = useRef<Map<string, CachedProjectData>>(new Map());

  const fetchOrGetCached = useCallback(async (projectName: string): Promise<CachedProjectData | null> => {
    if (!projectName) return null;

    // Check cache with TTL
    const cached = cacheRef.current.get(projectName);
    if (cached && (Date.now() - cached.fetchedAt) < CACHE_TTL_MS) {
      return cached;
    }

    try {
      const projectResponse = await authFetch(
        projectsApi(`action=get_project&project_name=${encodeURIComponent(projectName)}`)
      );

      if (!projectResponse.ok) return null;

      const projectData = await projectResponse.json();
      const projectInfo = projectData.project || projectData;
      const projectId: number = projectInfo.id;
      const promptId: string | null = projectInfo.prompt_id || null;

      let senders: Sender[] = [];

      const sendersResponse = await authFetch(
        projectsApi(`action=get_senders&project_id=${projectId}`)
      );

      if (sendersResponse.ok) {
        const sendersData = await sendersResponse.json();
        const sendersArray = sendersData.senders || sendersData || [];
        senders = Array.isArray(sendersArray)
          ? sendersArray.map((s: any) => ({
              id: s.id,
              name: s.name,
              surname: s.surname,
              voice_id: s.voice_id,
              calendly_link: s.calendly_link,
              project_id: projectId,
              created_at: s.created_at || new Date().toISOString(),
              gender: s.gender,
            }))
          : [];
      }

      const entry: CachedProjectData = { projectId, promptId, senders, fetchedAt: Date.now() };
      cacheRef.current.set(projectName, entry);
      return entry;
    } catch (error) {
      console.error('Error fetching project details:', error);
      return null;
    }
  }, []);

  const invalidate = useCallback((projectName?: string) => {
    if (projectName) {
      cacheRef.current.delete(projectName);
    } else {
      cacheRef.current.clear();
    }
  }, []);

  return { fetchOrGetCached, invalidate };
}
